import { useEffect, useState } from "react";
import axios from "axios";
import { Star } from "lucide-react";
import { API } from "@/lib/api";

// Kompakte Sterne-Bewertung für den Hero-Bereich (inkl. AggregateRating-Schema).
export const HeroRatingBadge = ({ testid = "hero-rating-badge" }) => {
  const { data, failed } = useReviews();
  if (!data?.rating || !data?.reviews?.length) return null;
  return (
    <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2" data-testid="hero-rating-badge">
        <div className="flex items-center gap-0.5" aria-label={`${String(data.rating).replace(".", ",")} von 5 Sternen bei Google`}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Star key={i} size={15} className={i <= Math.round(data.rating) ? "text-amber-400 fill-amber-400" : "text-white/30"} />
          ))}
        </div>
        <span className="text-sm font-bold text-white">{String(data.rating).replace(".", ",")}</span>
        <span className="w-px h-4 bg-white/25" aria-hidden="true" />
        <span className="text-xs text-slate-200 font-medium">{data.total} Google-Bewertungen</span>
      </div>
  );
};

const reviewCache = { data: null, failed: false };

export const useReviews = () => {
  const [data, setData] = useState(reviewCache.data);
  const [failed, setFailed] = useState(reviewCache.failed);

  useEffect(() => {
    if (reviewCache.data || reviewCache.failed) return;
    axios.get(`${API}/reviews`)
      .then((r) => {
        if (r.data && r.data.enabled !== false && r.data.rating && r.data.reviews?.length) {
          reviewCache.data = r.data;
          setData(r.data);
        } else {
          reviewCache.failed = true;
          setFailed(true);
        }
      })
      .catch(() => { reviewCache.failed = true; setFailed(true); });
  }, []);

  return { data, failed };
};

// Echte Google-Bewertungen über die Places API (Backend-Proxy /api/reviews).
// Ohne konfigurierte Place-ID/Key liefert das Backend {"enabled": false} →
// Bereich bleibt komplett ausgeblendet. Niemals erfundene Bewertungen zeigen.
// Hinweis SEO: Bewusst KEIN AggregateRating/Review-Schema mehr – selbst
// ausgezeichnete Sternebewertungen für das eigene Unternehmen wertet Google
// nicht aus und sie verstoßen gegen die Richtlinien für Rezensions-Snippets.
export const ReviewsBlock = ({ compact = false, testid = "reviews-block" }) => {
  const { data, failed } = useReviews();

  if (failed || !data) return null;

  const shown = data.reviews.slice(0, compact ? 3 : 6);

  return (
    <section className={compact ? "" : "py-16 bg-white"} data-testid={testid}>
      <div className={compact ? "" : "max-w-7xl mx-auto px-4 sm:px-6"}>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1" aria-label={`${String(data.rating).replace(".", ",")} von 5 Sternen`}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Star key={i} size={compact ? 16 : 20} className={i <= Math.round(data.rating) ? "text-amber-400 fill-amber-400" : "text-slate-300"} />
            ))}
          </div>
          <p className="font-bold text-navy text-sm sm:text-base">
            {String(data.rating).replace(".", ",")} von 5 · {data.total} echte Google-Bewertungen
          </p>
        </div>
        <div className={`mt-${compact ? 4 : 8} grid gap-4 sm:grid-cols-2 lg:grid-cols-3`}>
          {shown.map((r, i) => (
            <figure key={i} className="bg-slate-50 border border-slate-200 rounded-xl p-5" data-testid={`${testid}-review-${i}`}>
              <div className="flex items-center gap-1 mb-2">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star key={s} size={13} className={s <= r.rating ? "text-amber-400 fill-amber-400" : "text-slate-300"} />
                ))}
              </div>
              <blockquote className="text-sm text-slate-700 leading-relaxed">„{r.text}“</blockquote>
              <figcaption className="mt-3 text-xs font-semibold text-navy">{r.author} <span className="font-normal text-slate-400">· {r.time}</span></figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
};
